import { useEffect, useMemo, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
} from '@material-ui/core';
import moment from 'moment';

/**
 * Columns that are technical identifiers rather than user-facing data.
 */
const DEFAULT_HIDDEN_COLUMNS = ['id', 'ssn', 'donor_num', 'donor_id'];

/**
 * Columns matching these names are rendered in the narrow "date" style.
 */
const DATE_COLUMN_PATTERN = /(^|_)(date|created|updated|modified|next)(_|$)/i;

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2})?/;

/**
 * Width tiers. Every column is measured against its own content (and its
 * header) and dropped into one of these buckets, so a table only consumes the
 * horizontal space its data actually needs. Previously every non-date column
 * carried a flat 220px minimum, which made a 7-column table 1540px wide and
 * forced the whole page to scroll sideways.
 */
const WIDTH_TIERS = {
    // Column has no value in any row: keep it visible but as thin as possible.
    blank: { minWidth: 56, maxWidth: 90, wrapHeader: true },
    // Codes, initials, flags, short numbers.
    compact: { minWidth: 72, maxWidth: 140, wrapHeader: true },
    // Names, short phrases.
    medium: { minWidth: 120, maxWidth: 220, wrapHeader: true },
    // Free-text comments.
    wide: { minWidth: 180, maxWidth: 320, wrapHeader: false },
};

/** Longest formatted value decides the tier. */
function resolveTier(maxValueLength) {
    if (maxValueLength === 0) return 'blank';
    if (maxValueLength <= 12) return 'compact';
    if (maxValueLength <= 40) return 'medium';
    return 'wide';
}

const useStyles = makeStyles((theme) => ({
    accordion: {
        marginBottom: theme.spacing(2),
        borderRadius: theme.spacing(1),
        boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
        border: `1px solid ${theme.palette.divider}`,
        '&:before': {
            display: 'none', // remove the default MUI top hairline
        },
        '&$expanded': {
            margin: `0 0 ${theme.spacing(2)}px 0`,
        },
    },
    expanded: {},
    summary: {
        borderRadius: theme.spacing(1),
        minHeight: 56,
        '&$expanded': {
            minHeight: 56,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    summaryContent: {
        alignItems: 'center',
        '&$expanded': {
            margin: '12px 0',
        },
    },
    heading: {
        fontWeight: 600,
        color: theme.palette.primary.main,
        letterSpacing: '0.02em',
    },
    countChip: {
        marginLeft: theme.spacing(1.5),
        height: 22,
        fontSize: '0.75rem',
        fontWeight: 600,
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
    },
    details: {
        padding: 0,
        flexDirection: 'column',
        // A column flex container gives its children `min-width: auto`, which
        // lets a wide table push the accordion — and with it the whole page —
        // past the viewport. Pinning the minimum to 0 keeps the overflow
        // inside the table's own scroller.
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
    },
    tableContainer: {
        maxHeight: 420,
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        overflowX: 'auto',
    },
    table: {
        width: '100%',
    },
    headCell: {
        fontWeight: 600,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        lineHeight: 1.25,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.grey[50],
        borderBottom: `2px solid ${theme.palette.divider}`,
        whiteSpace: 'nowrap',
        padding: theme.spacing(0.75, 1.25),
    },
    row: {
        '&:nth-of-type(even)': {
            backgroundColor: theme.palette.action.hover,
        },
        '&:hover': {
            backgroundColor: theme.palette.action.selected,
        },
    },
    cell: {
        fontSize: '0.8125rem',
        color: theme.palette.text.primary,
        verticalAlign: 'top',
        padding: theme.spacing(0.75, 1.25),
    },
    dateCell: {
        whiteSpace: 'nowrap',
        fontVariantNumeric: 'tabular-nums',
        color: theme.palette.text.secondary,
        width: 1, // shrink-to-fit: dates never need more than their own text
    },
    // Content-sized tiers replacing the old blanket 220px minimum.
    blankCell: {
        minWidth: WIDTH_TIERS.blank.minWidth,
        maxWidth: WIDTH_TIERS.blank.maxWidth,
        textAlign: 'center',
    },
    compactCell: {
        minWidth: WIDTH_TIERS.compact.minWidth,
        maxWidth: WIDTH_TIERS.compact.maxWidth,
        whiteSpace: 'nowrap',
    },
    mediumCell: {
        minWidth: WIDTH_TIERS.medium.minWidth,
        maxWidth: WIDTH_TIERS.medium.maxWidth,
        wordBreak: 'break-word',
    },
    wideCell: {
        minWidth: WIDTH_TIERS.wide.minWidth,
        maxWidth: WIDTH_TIERS.wide.maxWidth,
        wordBreak: 'break-word',
    },
    // Declared after the tier classes so it wins on specificity: short and
    // empty columns let their header wrap onto a second line rather than
    // forcing the column open just to fit the label on one line.
    headCellWrap: {
        whiteSpace: 'normal',
    },
    emptyValue: {
        color: theme.palette.text.disabled,
    },
    statusBox: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(2),
    },
    errorBox: {
        padding: theme.spacing(1.5, 2),
        margin: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.dark,
        fontSize: '0.8125rem',
    },
    chevron: {
        transition: 'transform 150ms ease',
        color: theme.palette.text.secondary,
    },
}));

const ChevronIcon = ({ className }) => (
    <svg
        className={className}
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

/** Turns "deferral_expire_date" into "Deferral Expire Date". */
function humanizeColumn(column) {
    return column
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());
}

function formatCellValue(value, column) {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    const stringValue = String(value);

    if (DATE_COLUMN_PATTERN.test(column) && ISO_DATE_PATTERN.test(stringValue)) {
        const parsed = moment(stringValue);

        if (parsed.isValid()) {
            const hasTime = /[T ]\d{2}:\d{2}/.test(stringValue);
            return parsed.format(hasTime ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY');
        }
    }

    return stringValue;
}

/**
 * Fetches a JSON array from an endpoint and renders it as a collapsible table.
 * Renders nothing when the endpoint returns no rows.
 */
export default function DataList({
    heading,
    endpointUrl,
    query = {},
    initiallyOpen = false,
    hiddenColumns = DEFAULT_HIDDEN_COLUMNS,
}) {
    const classes = useStyles();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isOpen, setIsOpen] = useState(initiallyOpen);

    // Serialize so an inline object literal from the parent does not
    // recreate the memo dependency on every render.
    const serializedQuery = JSON.stringify(query ?? {});

    const requestUrl = useMemo(() => {
        const baseUrl = process.env.REACT_APP_DATA_PROVIDER_URL?.trim() ?? '';
        const suppliedEndpoint = endpointUrl?.trim() ?? '';

        // Treat HTTP, HTTPS, and protocol-relative URLs as complete URLs.
        const isFullUrl = /^(?:https?:)?\/\//i.test(suppliedEndpoint);

        let resolvedEndpoint = suppliedEndpoint;

        if (!isFullUrl) {
            if (!baseUrl) {
                return null;
            }

            resolvedEndpoint = `${baseUrl.replace(/\/+$/, '')}/${suppliedEndpoint.replace(
                /^\/+/,
                '',
            )}`;
        }

        const searchParameters = new URLSearchParams();

        Object.entries(JSON.parse(serializedQuery)).forEach(([key, value]) => {
            if (value === undefined || value === null) {
                return;
            }

            searchParameters.set(
                key,
                typeof value === 'object' ? JSON.stringify(value) : String(value),
            );
        });

        const queryString = searchParameters.toString();

        return queryString
            ? `${resolvedEndpoint}${resolvedEndpoint.includes('?') ? '&' : '?'}${queryString}`
            : resolvedEndpoint;
    }, [endpointUrl, serializedQuery]);

    useEffect(() => {
        if (!requestUrl) {
            setLoading(false);
            setError(
                new Error(
                    'REACT_APP_DATA_PROVIDER_URL is required when endpointUrl is not a full URL.',
                ),
            );
            return undefined;
        }

        const controller = new AbortController();

        async function loadData() {
            setLoading(true);
            setError(null);

            try {
                // The older *list endpoints are unauthenticated and ignore
                // this; newer ones require it. Sending it always means a panel
                // does not have to care which kind it is talking to.
                const headers = { Accept: 'application/json' };
                const token = localStorage.getItem('token');
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                }

                const response = await fetch(requestUrl, {
                    signal: controller.signal,
                    headers,
                });

                if (!response.ok) {
                    throw new Error(`Request failed with status ${response.status}`);
                }

                const contentType = response.headers.get('content-type') ?? '';

                if (!contentType.toLowerCase().includes('json')) {
                    throw new Error(
                        `Expected JSON from ${requestUrl}, but received ${
                            contentType || 'a non-JSON response'
                        }.`,
                    );
                }

                const result = await response.json();

                if (!Array.isArray(result)) {
                    throw new Error('The endpoint did not return a JSON array.');
                }

                setData(result);
            } catch (requestError) {
                if (requestError.name !== 'AbortError') {
                    setData([]);
                    setError(requestError);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        }

        loadData();

        return () => controller.abort();
    }, [requestUrl]);

    const columns = useMemo(() => {
        const discovered = Array.from(
            new Set(
                data.flatMap((item) =>
                    typeof item === 'object' && item !== null ? Object.keys(item) : [],
                ),
            ),
        ).filter((column) => !hiddenColumns.includes(column));

        // Surface date-like columns first so rows read chronologically.
        const dateColumns = discovered.filter((column) => DATE_COLUMN_PATTERN.test(column));
        const otherColumns = discovered.filter((column) => !DATE_COLUMN_PATTERN.test(column));

        const ordered = [...dateColumns, ...otherColumns];

        // Measure each column against the data it actually holds so the table
        // claims only the width it needs.
        const measured = ordered.map((column) => {
            const isDate = DATE_COLUMN_PATTERN.test(column);

            const maxValueLength = data.reduce((longest, item) => {
                const formatted = formatCellValue(item?.[column], column);
                return Math.max(longest, formatted.length);
            }, 0);

            return {
                name: column,
                label: humanizeColumn(column),
                isDate,
                isEmpty: maxValueLength === 0,
                tier: isDate ? 'date' : resolveTier(maxValueLength),
            };
        });

        // Columns with no data anywhere are pushed to the end: they still
        // appear (so the absence of data stays visible) but they no longer sit
        // between columns the user is actually reading.
        return [
            ...measured.filter((column) => !column.isEmpty),
            ...measured.filter((column) => column.isEmpty),
        ];
    }, [data, hiddenColumns]);

    const tierClassName = {
        date: classes.dateCell,
        blank: classes.blankCell,
        compact: classes.compactCell,
        medium: classes.mediumCell,
        wide: classes.wideCell,
    };

    // Nothing to show and nothing went wrong: stay out of the way entirely.
    if (!loading && !error && data.length === 0) {
        return null;
    }

    return (
        <Accordion
            expanded={isOpen}
            onChange={(event, expanded) => setIsOpen(expanded)}
            classes={{ root: classes.accordion, expanded: classes.expanded }}
            TransitionProps={{ unmountOnExit: true }}
        >
            <AccordionSummary
                classes={{
                    root: classes.summary,
                    content: classes.summaryContent,
                    expanded: classes.expanded,
                }}
                expandIcon={<ChevronIcon className={classes.chevron} />}
            >
                <Typography variant="subtitle1" className={classes.heading}>
                    {heading}
                </Typography>
                {!loading && !error && (
                    <Chip size="small" label={data.length} className={classes.countChip} />
                )}
            </AccordionSummary>

            <AccordionDetails className={classes.details}>
                {loading && (
                    <div className={classes.statusBox} role="status">
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="textSecondary">
                            Loading {heading?.toLowerCase()}…
                        </Typography>
                    </div>
                )}

                {error && (
                    <div className={classes.errorBox} role="alert">
                        Unable to load data: {error.message}
                    </div>
                )}

                {!loading && !error && (
                    <TableContainer className={classes.tableContainer}>
                        <Table size="small" stickyHeader className={classes.table}>
                            <TableHead>
                                <TableRow>
                                    {columns.map((column) => (
                                        <TableCell
                                            key={column.name}
                                            scope="col"
                                            className={`${classes.headCell} ${
                                                column.tier !== 'date' &&
                                                WIDTH_TIERS[column.tier]?.wrapHeader
                                                    ? classes.headCellWrap
                                                    : ''
                                            } ${tierClassName[column.tier] ?? ''}`}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {data.map((item, index) => (
                                    <TableRow
                                        key={item?.id ?? `${heading}-${index}`}
                                        className={classes.row}
                                    >
                                        {columns.map((column) => {
                                            const value = formatCellValue(
                                                item?.[column.name],
                                                column.name,
                                            );

                                            return (
                                                <TableCell
                                                    key={column.name}
                                                    className={`${classes.cell} ${
                                                        tierClassName[column.tier] ?? ''
                                                    }`}
                                                >
                                                    {value === '' ? (
                                                        <span className={classes.emptyValue}>—</span>
                                                    ) : (
                                                        value
                                                    )}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </AccordionDetails>
        </Accordion>
    );
}
